import { useId, useState } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { Helmet } from 'react-helmet-async';
import {
  ActionableNotification,
  Button,
  InlineLoading,
  InlineNotification,
} from '@carbon/react';
import { ArrowLeft } from '@carbon/icons-react';
import routes from '~/routes';
import { ExportImageDrawer } from 'components/chart-export';
import { pageTitle } from 'utils/page-title';
import { AnalysisPanel } from './components/analysis-panel/analysis-panel';
import { FactorOrder } from './components/factor-order/factor-order';
import { RunProgress } from './components/run-progress/run-progress';
import {
  AnalyzeBreadcrumb,
  type AnalyzeCrumb,
} from './components/analyze-breadcrumb/analyze-breadcrumb';
import {
  ComparisonResults,
  type DrillState,
} from './components/comparison-results/comparison-results';
import {
  useComparisonAnalysis,
  useCreateComparisonRun,
  useEvalPair,
  useFactorOrder,
  useResultsRun,
  useRunId,
} from './hooks';
import {
  analyzeHref,
  compareResultHref,
  withRunId,
  formatMillions,
  formatPercent,
  formatSignedMillions,
  isRunLive,
  percentChange,
  rescatScopeSuffix,
} from './utils';
import type { RunResponse } from './types';
import styles from './content.module.scss';

// "Analyze Value Drivers" (INS-11749) — the eval-3-vs-eval-3 NPV comparison launched
// from the Package Comparison result page. The Origin/Target pair lives in the URL
// (`side_a_evaluation_id` / `side_b_evaluation_id`), so every step is deep-linkable
// and the analysis is fetched (and cached) per pair via React Query.

/**
 * The guard shown when a step is reached without a valid eval pair in the URL
 * (e.g. a bare deep link). Routes the user back to select two evaluations.
 *
 * @return {JSX.Element} An info notice + "Back to Compare Evaluations" action.
 */
function MissingPairNotice() {
  const history = useHistory();
  return (
    <>
      <InlineNotification
        kind="info"
        lowContrast
        hideCloseButton
        title="No evaluations selected"
        subtitle="Start from Compare Evaluations and open the comparison for two Eval 3 evaluations, then choose Analyze Value Drivers."
      />
      <div>
        <Button
          kind="ghost"
          renderIcon={ArrowLeft}
          onClick={() => history.push(routes.compareWizardPath)}
        >
          Back to Compare Evaluations
        </Button>
      </div>
    </>
  );
}

/**
 * Step 2 — Comparison Analysis. Reads the pair from the URL, auto-runs the analysis
 * (keyed + cached per pair), and renders the tiles/chips. "Proceed" advances to
 * Factor Ordering, threading the pair forward. A loaded analysis stays on screen even if a later
 * refetch fails — the error shows only when there's nothing to show.
 *
 * @return {JSX.Element} The analysis landing page.
 */
export function AnalyzePage() {
  const history = useHistory();
  const pair = useEvalPair();
  const query = useComparisonAnalysis(
    pair?.origin ?? null,
    pair?.target ?? null
  );

  return (
    <div className={styles.page}>
      <Helmet>
        <title>{pageTitle('Analyze Value Drivers')}</title>
      </Helmet>

      <AnalyzeBreadcrumb
        items={[
          {
            label: 'Compare Evaluations',
            href: pair
              ? compareResultHref(pair.origin, pair.target)
              : routes.compareWizardPath,
          },
          { label: 'Analyze Value Drivers' },
        ]}
      />

      <div className={styles.header}>
        <h1 className={styles.title}>Analyze Value Drivers</h1>
        <p className={styles.subtitle}>
          Identify and quantify the input factors that drive the NPV difference
          between the Origin and Target evaluation.
        </p>
      </div>

      <div className={styles.body}>
        {!pair ? (
          <MissingPairNotice />
        ) : query.isLoading ? (
          <InlineLoading description="Running comparison analysis…" />
        ) : query.data ? (
          <section className={styles.card}>
            <div className={styles.cardHeader}>Comparison Analysis</div>
            <div className={styles.cardBody}>
              <AnalysisPanel
                analysis={query.data}
                onProceed={() =>
                  history.push(
                    analyzeHref(
                      routes.compareAnalyzeFactorOrderPath,
                      pair.origin,
                      pair.target
                    )
                  )
                }
              />
            </div>
          </section>
        ) : query.isError ? (
          <InlineNotification
            kind="error"
            lowContrast
            title="Comparison analysis failed"
            subtitle={query.error?.message ?? 'Something went wrong.'}
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * Step 3 — Factor Ordering. Reads the same cached analysis and lets the user order
 * the factor groups the engine steps through. Back returns to Analysis; Run creates the run
 * and opens its Results at once (which follow it until it finishes). Both thread the pair forward.
 * Like Step 2, a loaded analysis outlasts a failed refetch.
 *
 * @return {JSX.Element} The factor-ordering page.
 */
export function AnalyzeFactorOrderPage() {
  const history = useHistory();
  const pair = useEvalPair();
  const query = useComparisonAnalysis(
    pair?.origin ?? null,
    pair?.target ?? null
  );
  const { groups, totalChanged, iterations, reorder } = useFactorOrder(
    query.data
  );
  const createRun = useCreateComparisonRun();

  const analyzePath = pair
    ? analyzeHref(routes.compareAnalyzePath, pair.origin, pair.target)
    : routes.compareWizardPath;

  // Create the run, then open Results at once: it follows the run's stages live and shows the
  // chart and table once every factor is in (its URL carries only run_id, so it survives a reload).
  const handleRun = () => {
    if (!pair) return;
    createRun.mutate(
      { pair, factorOrder: groups.map((group) => group.key) },
      {
        onSuccess: (run) =>
          history.push(withRunId(routes.compareAnalyzeResultsPath, run.id)),
      }
    );
  };

  return (
    <div className={styles.page}>
      <Helmet>
        <title>{pageTitle('Factor Ordering')}</title>
      </Helmet>

      <AnalyzeBreadcrumb
        items={[
          {
            label: 'Compare Evaluations',
            href: pair
              ? compareResultHref(pair.origin, pair.target)
              : routes.compareWizardPath,
          },
          { label: 'Analyze Value Drivers', href: analyzePath },
          { label: 'Factor Ordering' },
        ]}
      />

      <div className={styles.header}>
        <h1 className={styles.title}>Factor Ordering</h1>
        <p className={styles.subtitle}>
          Drag or use the arrows to set the order in which the comparison engine
          steps through each factor group. The engine switches one factor at a
          time from Origin to Target and recomputes.
        </p>
      </div>

      <div className={styles.body}>
        {!pair ? (
          <MissingPairNotice />
        ) : query.isLoading ? (
          <InlineLoading description="Running comparison analysis…" />
        ) : query.data ? (
          <>
            <FactorOrder
              groups={groups}
              totalChanged={totalChanged}
              iterations={iterations}
              onReorder={reorder}
              onBack={() => history.push(analyzePath)}
              onRun={handleRun}
              isRunPending={createRun.isPending}
            />
            {createRun.isError && (
              <InlineNotification
                kind="error"
                lowContrast
                title="Couldn't start the comparison"
                subtitle={createRun.error?.message ?? 'Something went wrong.'}
              />
            )}
          </>
        ) : query.isError ? (
          <>
            <InlineNotification
              kind="error"
              lowContrast
              title="Comparison analysis failed"
              subtitle={query.error?.message ?? 'Something went wrong.'}
            />
            <div>
              <Button
                kind="ghost"
                renderIcon={ArrowLeft}
                onClick={() => history.push(analyzePath)}
              >
                Back to Analysis
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Renders a run's outcome by status: a non-viable/failed run shows a notice with the
 * reason (a failed run also shows its stages, so the stopped one is visible); a viable run
 * hands off to {@link ComparisonResults} (the group-level NPV
 * waterfall with its group → factor → slot drill-down, scoped by the reserve category).
 *
 * @param {object} props - The run, drill state, and reserve-category filter.
 * @param {RunResponse} props.run - The comparison run.
 * @param {DrillState | null} props.drill - The current drill level (page-owned).
 * @param {Function} props.onDrill - Drill down into a bar.
 * @param {string | null} props.rescat - The page's reserve category (null = all).
 * @param {Function} props.onRescatChange - Change the reserve category.
 * @param {boolean} props.isRescatPending - A category switch is loading.
 * @param {Error | null} props.rescatError - The last category switch failed.
 * @return {JSX.Element} The run's result view.
 */
function RunResult({
  run,
  drill,
  onDrill,
  rescat,
  onRescatChange,
  isRescatPending,
  rescatError,
}: {
  run: RunResponse;
  drill: DrillState | null;
  onDrill: (drill: DrillState) => void;
  rescat: string | null;
  onRescatChange: (rescat: string | null) => void;
  isRescatPending: boolean;
  rescatError: Error | null;
}) {
  if (run.status === 'non_viable') {
    return (
      <InlineNotification
        kind="info"
        lowContrast
        hideCloseButton
        title="Comparison unavailable"
        subtitle={run.errorReason ?? 'These evaluations cannot be compared.'}
      />
    );
  }

  if (run.status === 'failed') {
    // A run that fails stays on Results: show the stage it stopped at under the notice.
    return (
      <>
        <InlineNotification
          kind="error"
          lowContrast
          title="Comparison failed"
          subtitle={run.errorReason ?? 'The comparison engine hit an error.'}
        />
        <RunProgress progress={run.progress} />
      </>
    );
  }

  return (
    <ComparisonResults
      run={run}
      drill={drill}
      onDrill={onDrill}
      rescat={rescat}
      onRescatChange={onRescatChange}
      isRescatPending={isRescatPending}
      rescatError={rescatError}
    />
  );
}

/**
 * Step 4 — Comparison Results. Reads the run id from the URL, streams the run's NPV
 * waterfall (React Query + Pusher), and renders the group-level chart with its
 * drill-down. A bare deep link (no run) is guarded back to Factor Ordering.
 *
 * @return {JSX.Element} The results page.
 */
export function AnalyzeResultsPage() {
  const history = useHistory();
  const runId = useRunId();
  // Where keyboard focus waits while a failed load retries (its notice is replaced as it loads).
  const titleId = useId();
  const {
    run,
    query,
    rescat,
    setRescat,
    isRescatPending,
    rescatError,
    refreshError,
  } = useResultsRun(runId);
  // The page stays mounted across a `?run_id=` change (only the search changes), so the drill
  // belongs to one run: a prior run's drill/labels must not leak into a new one. Reset during
  // render, the same way useResultsRun resets the category.
  const [drillState, setDrillState] = useState<{
    runId: number | null;
    drill: DrillState | null;
  }>({ runId, drill: null });
  if (drillState.runId !== runId) setDrillState({ runId, drill: null });
  const drill = drillState.runId === runId ? drillState.drill : null;
  const setDrill = (next: DrillState | null) =>
    setDrillState({ runId, drill: next });

  // The Results URL carries only run_id; the Origin/Target pair for the ancestor links
  // is derived from the run itself (the run response returns both eval ids).
  const compareRoot = run
    ? compareResultHref(
        run.originEvaluationCalculationId,
        run.targetEvaluationCalculationId
      )
    : routes.compareWizardPath;
  const analyzePath = run
    ? analyzeHref(
        routes.compareAnalyzePath,
        run.originEvaluationCalculationId,
        run.targetEvaluationCalculationId
      )
    : routes.compareWizardPath;
  const factorOrderPath = run
    ? analyzeHref(
        routes.compareAnalyzeFactorOrderPath,
        run.originEvaluationCalculationId,
        run.targetEvaluationCalculationId
      )
    : routes.compareWizardPath;

  // The full breadcrumb: the wizard trail plus, on Results, the live drill path — one
  // breadcrumb that also pops the drill levels (Results / Group are clickable).
  const crumbs: AnalyzeCrumb[] = [
    { label: 'Compare Evaluations', href: compareRoot },
    { label: 'Analyze Value Drivers', href: analyzePath },
    { label: 'Factor Ordering', href: factorOrderPath },
  ];
  if (!drill) {
    crumbs.push({ label: 'Results' });
  } else {
    crumbs.push({ label: 'Results', onClick: () => setDrill(null) });
    if (drill.factorKey) {
      crumbs.push({
        label: drill.groupLabel,
        onClick: () =>
          setDrill({
            groupKey: drill.groupKey,
            groupLabel: drill.groupLabel,
            factorKey: null,
            factorLabel: null,
          }),
      });
      crumbs.push({ label: drill.factorLabel ?? drill.factorKey });
    } else {
      crumbs.push({ label: drill.groupLabel });
    }
  }

  // The headline NPV delta (Target − Origin), from the run's own anchor bars; its percent follows
  // the Summary Table's rule, so the header matches the Total row.
  const originBar = run?.waterfall.find((bar) => bar.key === 'origin') ?? null;
  const targetBar = run?.waterfall.find((bar) => bar.key === 'target') ?? null;
  const npvDeltaPct =
    originBar && targetBar
      ? percentChange(targetBar.value - originBar.value, originBar.value)
      : null;

  return (
    <div className={styles.page}>
      <Helmet>
        <title>{pageTitle('Comparison Results')}</title>
      </Helmet>

      <AnalyzeBreadcrumb items={crumbs} />

      <div className={styles.header}>
        <h1 id={titleId} tabIndex={-1} className={styles.title}>
          Comparison Results
        </h1>
        <p className={styles.subtitle}>
          {originBar && targetBar ? (
            <>
              Origin {formatMillions(originBar.value)}{' '}
              <span aria-hidden>→</span> Target{' '}
              {formatMillions(targetBar.value)}
              {' · '}NPV delta{' '}
              <strong>
                {formatSignedMillions(targetBar.value - originBar.value)}
              </strong>
              {npvDeltaPct !== null ? ` (${formatPercent(npvDeltaPct)})` : ''}
              {rescatScopeSuffix(run?.rescat ?? null)}
            </>
          ) : (
            'The NPV difference between the Origin and Target evaluation, attributed to each input factor.'
          )}
        </p>
      </div>

      {/* The results, with the Export Image drawer their chart's export trigger opens beside them. */}
      <div className={styles.resultsRow}>
        <div className={classNames(styles.body, styles.resultsBody)}>
          {runId === null ? (
            <InlineNotification
              kind="info"
              lowContrast
              hideCloseButton
              title="No comparison run"
              subtitle="Start the comparison from Factor Ordering to see the value-driver waterfall."
            />
          ) : run ? (
            <>
              {refreshError && (
                // A finished run pushes no more updates, so a failed refresh can't fix itself —
                // the user retries it.
                <ActionableNotification
                  inline
                  kind="warning"
                  // hasFocus off: its auto-focus would scroll the page to the notice
                  hasFocus={false}
                  lowContrast
                  hideCloseButton
                  title="Couldn't refresh the comparison"
                  subtitle={`${
                    isRunLive(run.status)
                      ? "The run's latest progress couldn't be loaded."
                      : 'Showing the last loaded results.'
                  } ${refreshError.message}`}
                  actionButtonLabel="Try again"
                  onActionButtonClick={() => query.refetch()}
                />
              )}
              <RunResult
                run={run}
                drill={drill}
                onDrill={setDrill}
                rescat={rescat}
                onRescatChange={setRescat}
                isRescatPending={isRescatPending}
                rescatError={rescatError}
              />
            </>
          ) : query.isError ? (
            // A read that failed just now (a 503) is worth retrying in place.
            <ActionableNotification
              inline
              kind="error"
              // hasFocus off: its auto-focus would scroll the page to the notice
              hasFocus={false}
              lowContrast
              hideCloseButton
              title="Couldn't load the comparison"
              subtitle={query.error?.message ?? 'Something went wrong.'}
              actionButtonLabel="Try again"
              onActionButtonClick={() => {
                document.getElementById(titleId)?.focus();
                query.refetch();
              }}
            />
          ) : (
            <InlineLoading description="Loading results…" />
          )}

          {/* Overview only — while drilled in, the breadcrumb is the way back. */}
          {!drill && (
            <div>
              <Button
                kind="ghost"
                renderIcon={ArrowLeft}
                onClick={() => history.push(factorOrderPath)}
              >
                Back to Factor Ordering
              </Button>
            </div>
          )}
        </div>
        <ExportImageDrawer />
      </div>
    </div>
  );
}
